import {Alert, Loader, Stack, Text} from '@mantine/core';
import React, {useEffect} from 'react';

import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ScientificModelRegistry,
  useScientificModelRegistry,
} from 'model-hub/scientific-models/useAvailableScientificModels';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';

import FolderEditor from '../editors/FolderEditor';
import StepConfigFieldsTemplate from '../editors/StepConfigFieldsTemplate';
import useTaggedUnionFields from '../editors/useTaggedUnionFields';
import {maybeGetValueFromConfig} from '../step-config-settings/StepConfigSettingsUtils';
import {StepTypeConfigUISchemaV2} from '../step-config-settings/StepConfigUISchemas';
import {
  CustomComponentEditorProps,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
} from '../StepConfigEditorTypes';

const MODEL_CONFIG_KEY = 'modelConfig';
const PREDICTION_BATCH_FOLDER_ID = 'predictionBatchFolderId';

const MODEL_CONFIG_SHAPE = PIPELINE_STEP_SHAPES[PipelineStepType.RUN_SCIENTIFIC_MODEL][0].config.modelConfig;

function ScientificModelConfigEditor(props: CustomComponentEditorProps): JSX.Element {
  const scientificModelRegistry = useScientificModelRegistry();
  if (scientificModelRegistry.type === 'loading') {
    return <Loader size="sm" role="status" aria-label="Loading models" />;
  }
  if (scientificModelRegistry.type === 'error') {
    return (
      <Alert color="red" role="alert">
        Unable to load models. Please refresh the page.
      </Alert>
    );
  }
  return <LoadedScientificModelConfigEditor {...props} registry={scientificModelRegistry.registry} />;
}

function LoadedScientificModelConfigEditor({
  config,
  onChangeConfig,
  disabled,
  registry,
}: CustomComponentEditorProps & {registry: ScientificModelRegistry}): JSX.Element {
  const predictionBatchFolderId = maybeGetValueFromConfig(config, [PREDICTION_BATCH_FOLDER_ID]) as
    | string
    | null;

  useEffect(() => {
    const defaultFolderId = FolderDefaults.get(FolderDefaultsKey.BULK_MODEL_PREDICTION);
    if (!disabled && predictionBatchFolderId == null && defaultFolderId != null) {
      onChangeConfig(config, [PREDICTION_BATCH_FOLDER_ID], defaultFolderId);
    }
  }, [config, disabled, onChangeConfig, predictionBatchFolderId]);

  const selectedModel = maybeGetValueFromConfig(config, [MODEL_CONFIG_KEY, 'model']) as string | null;
  const selectedModelNode =
    selectedModel == null ? undefined : registry.catalogByExternalReference.get(selectedModel);

  const availableModels = MODEL_CONFIG_SHAPE.choices.filter(
    ({fields: {model}}) =>
      model.value === selectedModel || registry.availableModelsByExternalReference.has(model.value)
  );
  const modelDisplayNames = Object.fromEntries(
    availableModels.map(({fields: {model}}) => [
      model.value,
      registry.catalogByExternalReference.get(model.value)?.displayName ?? model.value,
    ])
  );

  const {tagField, choiceFields} = useTaggedUnionFields({
    config,
    configShape: {...MODEL_CONFIG_SHAPE, choices: availableModels},
    configShapePath: [MODEL_CONFIG_KEY],
    onChangeConfig,
    disabled,
    fieldSettings: {
      tagKeyDisplayName: 'Model',
      placeholder: 'Select a model',
      tagKeyDisplayNames: modelDisplayNames,
      choiceFieldOrder: ['heavyChainColumn', 'lightChainColumn'],
      choiceUISchema: {
        heavyChainColumn: {
          displayName: 'Heavy-chain sequence column',
          configShapePath: ['heavyChainColumn'],
        },
        lightChainColumn: {
          displayName: 'Light-chain sequence column',
          configShapePath: ['lightChainColumn'],
        },
      },
    },
  });

  const predictionBatchFolderField = {
    variableName: 'Prediction batch folder',
    required: true,
    editor: (
      <FolderEditor
        value={predictionBatchFolderId}
        onChange={(folderId) => {
          onChangeConfig(config, [PREDICTION_BATCH_FOLDER_ID], folderId);
          if (folderId != null) {
            FolderDefaults.set(FolderDefaultsKey.BULK_MODEL_PREDICTION, folderId);
          }
        }}
        required={true}
        disabled={disabled}
        showRegistries={false}
        showInventory={false}
        showNotebook={true}
      />
    ),
  };

  if (availableModels.length === 0) {
    return <Text size="sm">No scientific models are installed on this tenant.</Text>;
  }
  return (
    <Stack spacing="sm">
      <Text size="md" mb="md" ml="xs">
        This step runs a scientific model for each input row.
      </Text>
      <Stack spacing={0}>
        <StepConfigFieldsTemplate numColumns={1} fields={[predictionBatchFolderField]} />
        <StepConfigFieldsTemplate numColumns={1} fields={[{...tagField, required: true}]} />
        {selectedModelNode?.description && (
          <Text size="sm" color="dimmed" mb="lg" ml="xs">
            {selectedModelNode.description}
          </Text>
        )}
        {selectedModel != null && (
          <StepConfigFieldsTemplate
            numColumns={2}
            fields={choiceFields.map((field) => ({...field, required: true}))}
          />
        )}
      </Stack>
    </Stack>
  );
}

export const RUN_SCIENTIFIC_MODEL_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.RUN_SCIENTIFIC_MODEL,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [MODEL_CONFIG_KEY],
  modelConfig: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: [MODEL_CONFIG_KEY],
    modelConfig: {
      [CUSTOM_COMPONENT_KEY]: ScientificModelConfigEditor,
    },
  },
};
